import { FC, useContext, useEffect, useMemo, useRef } from "react";
import { Route, useRouteMatch } from "react-router-dom";
import ZipProjectContextProvider, { ZipProjectContext } from "./ZipProjectContext";
import { ZipCodeTable } from "./ZipCodeTable";
import { ZipProjectModuleRoutesEnum, ZipProjectRouteComponentProps } from "./interfaces";
import { BreadcrumbTemplateEnum, Breadcrumbs, LoadingSpinner, Page, PageHeader, PageHeaderLeft, PageInner, TooltipWrapper } from "@app/shared";
import { ZipProjectAlertBanners } from "./ZipProjectAlertBanners";
import { ZipProjectNotificationsContainer } from "./zipProjectNotificationsContainer";
import { ZipProjectModuleNav } from "./ZipProjectModuleNav";
import { SummaryRouter } from "../network-assessment/summary";
import { ZipClassTable } from "./ZipClassTable";
import { ZipProjectPerspectiveNav } from "./ZipProjectSubModuleNav";
import { ProjectPerspective, ProjectRunConfigurationType } from "../enumerators";
import { ViewableFieldsContainer } from "@app/viewable-fields/ViewableFieldsContainer";
import { ZipProjectProviders } from "./ZipProjectProviders";
import { ZipProjectControls } from "./ZipProjectControls";
import { ZipProjectTimesliceManagement } from "./ZipProjectTimesliceManagement";
import { ZipProjectFilters } from "./ZipProjectFilters";
import { CheckboxDropdown, DropdownItem } from "@app/shared/checkbox-dropdown";
import { ZipChangedGapsFilter } from "./ZipChangedGapsFilter";
import { Flip, toast, ToastPosition } from "react-toastify";

const WrappedZipProjectOverview: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const { state } = projectContext;
  const match = useRouteMatch<ZipProjectRouteComponentProps>();
  ZipProjectNotificationsContainer();

  const statusContainerRef = useRef<HTMLDivElement>(null);
  let statusToastId: string | number = undefined;
  let canRenderStatusToast = true;

  const removeStatusToast = () => {
    if (!!statusToastId) {
      canRenderStatusToast = false;
      toast.dismiss(statusToastId);
      statusToastId = undefined;
    }
  }

  const renderStatusToast = () => {
    if (statusContainerRef.current && statusContainerRef.current.querySelectorAll(".qes-alert").length > 0 && canRenderStatusToast) {
      const statusContainer = statusContainerRef.current;
      const offScreen = statusContainer.getBoundingClientRect().bottom < (16 * 3.5); // 3.5rem, the height of looper's nav bar
      const toastSettings = {
        autoClose: false as const, // TS said it expects "number | false" and a boolean wasn't acceptable, so...
        closeButton: true,
        closeOnClick: true,
        containerId: "z-index-1030",
        hideProgressBar: true,
        // The -110 is somewhat arbitrary, but it should scroll up over the project title
        onClick: () => window.scrollTo({ behavior: "smooth", top: statusContainer.getBoundingClientRect().top + document.documentElement.scrollTop - 110 }),
        position: "top-center" as ToastPosition,
        transition: Flip,

      };
      if (offScreen) {
        if (!statusToastId) {
          const childClasses = Array.from(statusContainer.querySelectorAll(".qes-alert")).reduce((result, child) => result += child.className, "");
          const toastString = "Click here to view project notifications.";
          if (childClasses.includes("danger")) {
            statusToastId = toast.error(toastString, toastSettings);
          } else if (childClasses.includes("warning")) {
            statusToastId = toast.warn(toastString, toastSettings);
          } else {
            statusToastId = toast.info(toastString, toastSettings);
          }
        }
      } else {
        removeStatusToast();
      }
    } else {
      removeStatusToast();
    }
  }

  useEffect(() => {
    window.addEventListener("scroll", renderStatusToast);

    return () => {
      window.removeEventListener("scroll", renderStatusToast);
      removeStatusToast();
    };
  }, []);

  useEffect(() => {
    if (!state.project) {
      projectContext.onLoadPage(match.params.projectId);
    }
  }, []);

  const submodule = useMemo(() => {
    switch (state.perspective) {
      case ProjectPerspective.ByZipCode:
        return <ZipCodeTable />;
      case ProjectPerspective.ByZipClass:
        return <ZipClassTable />;
    }
  }, [state.perspective]);

  const dualMembershipOptions: DropdownItem<string>[] = useMemo(() => {
    if (!state.project?.projectRunConfigurations || state.runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.Standard) {
      return [];
    }

    const companionRunConfiguration = state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === state.runConfiguration.companionProjectRunConfigurationId);
    const primary = state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary ? state.runConfiguration : companionRunConfiguration;
    const secondary = primary.projectRunConfigurationId === companionRunConfiguration.projectRunConfigurationId ? state.runConfiguration : companionRunConfiguration;
    return [
      {
        key: primary.projectRunConfigurationId.toString(),
        name: primary.memberSetAlias || primary.memberSetName,
      },
      {
        key: secondary.projectRunConfigurationId.toString(),
        name: secondary.memberSetAlias || secondary.memberSetName,
      },
    ];
  }, [state.project, state.runConfiguration]);

  const selectedMemberships = useMemo(() => {
    if (!state.runConfiguration) {
      return [];
    }
    const selection = [{
      key: state.runConfiguration.projectRunConfigurationId.toString(),
      name: state.runConfiguration.memberSetAlias || state.runConfiguration.memberSetName,
    }];

    if (state.isDualMembershipMode && !!state.dualMembershipRunConfiguration) {
      selection.push({
        key: state.dualMembershipRunConfiguration.projectRunConfigurationId.toString(),
        name: state.dualMembershipRunConfiguration.memberSetAlias || state.dualMembershipRunConfiguration.memberSetName,
      })
    }

    return selection;
  }, [state.runConfiguration, state.isDualMembershipMode, state.dualMembershipRunConfiguration]);

  if (state.error)
    throw { status: state.error.status, statusText: state.error.statusText, traceId: state.error.responseJSON?.traceId };

  return (
    <Page documentTitle={`${state.project?.resource.name || "Loading"}`}>
      <Breadcrumbs template={BreadcrumbTemplateEnum.NetworkAssessment} items={[state.project?.resource.name || ""]} />
      <PageInner>
        <PageHeader title={state.project?.resource.name || ""} disableHR={true}>
          {!!state.project && (
            <PageHeaderLeft>
              <ZipProjectControls />
            </PageHeaderLeft>
          )}
        </PageHeader>
        <div ref={statusContainerRef}><ZipProjectAlertBanners /></div>
        {!!state.project?.projectId && !!state.runConfiguration ? (
          <>
            <ZipProjectModuleNav />
            <Route exact={false} path={`${match.path}/${ZipProjectModuleRoutesEnum.Adequacy}`}>
              <div className="mt-3 zip-project-overview">
                <ZipProjectPerspectiveNav />
                <div className="mt-4">
                  {!!state.runConfiguration.companionProjectRunConfigurationId
                    && (state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary
                      || state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental)
                    && (
                      <TooltipWrapper text={state.isCompareMode ? "Membership selection is disabled when comparing time slices" : ""}>
                        <CheckboxDropdown
                          buttonText={state.isDualMembershipMode ? "Display: Both Memberships" : `Display: ${state.runConfiguration.memberSetAlias}`}
                          cancelText="Cancel"
                          disabled={state.loading || state.isCompareMode}
                          disableEmptySelection={true}
                          loading={state.loading}
                          items={dualMembershipOptions}
                          onSubmit={(selectedItems: Array<DropdownItem<ProjectRunConfigurationType>>) => projectContext.onChangeSelectedMemberships(selectedItems)}
                          selectedItems={selectedMemberships}
                          submitText="Submit"
                        />
                      </TooltipWrapper>
                    )}
                  <ZipProjectTimesliceManagement />
                </div>
                <div className="mt-4">
                  <ZipProjectFilters />
                </div>
                <ZipChangedGapsFilter />
                {submodule}
                {state.perspective === ProjectPerspective.ByZipCode && state.isDrilledIn && (
                  <div className="mt-5">
                    <ViewableFieldsContainer
                      customProjectId={state.project.projectId}
                      projectRunConfigurationId={state.runConfiguration.projectRunConfigurationId}
                      specialtyGroupCode={state.filterSelections.specialtyGroupCode.key}
                    >
                      <ZipProjectProviders />
                    </ViewableFieldsContainer>
                  </div>
                )}
              </div>
            </Route>
            <Route exact={false} path={`${match.path}/${ZipProjectModuleRoutesEnum.Summary}`} component={SummaryRouter} />
          </>
        ) : state.loading ? <LoadingSpinner /> : null}
      </PageInner>
    </Page >
  );
}

const ZipProjectOverview: FC = () => (
  <ZipProjectContextProvider>
    <WrappedZipProjectOverview />
  </ZipProjectContextProvider>
);

export default ZipProjectOverview;
